import React, { useEffect, useState } from "react";
import PageWrapper from "../../components/PageWrapper";
import { useTranslation } from "react-i18next";
import { getAllProducts } from "../../services/productServices";
import HotdropdownV2 from "../../components/HotdropdownV2";
import axiosClient from "../../services/axiosClient";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import ProductOrderList from "./purchases/ProductOrderList";
import SectionDivider from "../../components/SectionDivider";
import { Component } from "lucide-react";
import { Link } from "react-router-dom";
import {
  CollapsibleContent,
  CollapsibleGroup,
  CollapsibleHeader,
  CollapsibleLabel,
  CollapsibleList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../../components/CollapsibleGroup";
import arrowIcon from "../../assets/previous_icon.png";
import CustomButton from "../../components/CustomButton";
import { getAllEducationLevels } from "../../services/educationLevelServices";
import CustomInput from "../../components/CustomInput";
import useDebounce from "../../utils/useDebounce";
import InformationBox from "../../components/InformationBox";
import ErrorBox from "../../components/ErrorBox";

const DeliveredSuppliesPage = () => {
  const { t } = useTranslation("general");

  const [selectedSupplie, setSelectedSupplie] = useState("books");

  const supplieOptions = [
    { id: "books", title: t("books") },
    { id: "uniform", title: t("uniform") },
  ];

  return (
    <PageWrapper withBackButton title={t("deliveredSupplies")}>
      <div className="o-column o-column--gap-size_200 u-width_full-width u-margin--top_700">
        <label>{t("supplieType")}</label>
        <div>
          <HotdropdownV2
            initOptions={supplieOptions}
            selected={supplieOptions.find(
              (belonging) => belonging.id === selectedSupplie
            )}
            setSelected={(selected) => {
              setSelectedSupplie(selected.id);
            }}
            optionsName="id"
          />
        </div>
      </div>
      {selectedSupplie === "books" && <SupplieBooks />}

      {selectedSupplie === "uniform" && <SupplieUniform />}
    </PageWrapper>
  );
};

function SupplieBooks() {
  const { t } = useTranslation("general");
  const [educationLevels, setEducationLevels] = useState([]);
  const [selectedAcademicLevel, setSelectedAcademicLevel] = useState({
    title: t("selectAll"),
    value: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const searchValue = useDebounce(searchQuery);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [booksData, setBooksData] = useState([]);
  const [errorBooks, setErrorBooks] = useState(false);

  // Book headers configuration
  const bookHeaders = [
    { key: "name", label: "bookName", type: "link", to: "" },
    { key: "productSubType", label: "bookType", type: "text" },
    { key: "amountInStock", label: "amountInStock", type: "text" },
    {
      key: "deliveredCount",
      label: "deliveredCount",
      type: "text",
      unit: "book",
    },
  ];

  const getAllBooks = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setErrorBooks(false);
      setLoadingBooks(true);
      setBooksData([]);
      const response = await axiosClient.get("/schoolClass/getAllWithProduct", {
        params: {
          bookName: searchValue,
          classId: selectedAcademicLevel.value,
        },
      });

      
      if (response.data?.schoolClasses) {
        setBooksData(response.data.schoolClasses);
      }

    } catch (err) {
      setErrorBooks("errorGettingBooks");
    } finally {
      setLoadingBooks(false);
    }
  };

  const fetchEducationLevels = async () => {
    try {
      const response = await getAllEducationLevels();

      const transformedData = (response.data.classes || []).map((level) => ({
        title: level.name,
        value: level.id,
        price: level.price,
        schoolSubClasses:
          level.schoolSubClasses?.map((subClass) => ({
            title: subClass.name,
            value: subClass.id,
          })) || [],
      }));

      setEducationLevels(transformedData);

      return {
        status: response.status || 200,
        data: {
          classes: transformedData,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        },
      };
    } catch (error) {
      console.error("Error fetching education levels:", error);
      return {
        status: 500,
        data: { classes: [], totalPages: 1, totalRecords: 0 },
      };
    }
  };

  const handleAcademicLevelChange = (selected) => {
    setSelectedAcademicLevel(selected);
  };

  const isThereBooks = !loadingBooks && booksData.length > 0 && !errorBooks;

  useEffect(() => {
    getAllBooks();
  }, [searchValue, selectedAcademicLevel.value]);

  useEffect(() => {
    fetchEducationLevels();
  }, []);

  return (
    <>
      <div className="o-column-row-switcher o-column--vertical-align_start o-row--gap-size_300">
        <div className="o-column o-column--gap-size_200 u-margin--top_400">
          <label>{t("academicLevel")}</label>
          <div>
            <HotdropdownV2
              initOptions={[
                ...educationLevels,
                { title: t("selectAll"), value: null },
              ]}
              selected={selectedAcademicLevel.title || ""}
              setSelected={handleAcademicLevelChange}
              page="students"
              getData={fetchEducationLevels}
              optionsName="classes"
              flex
            />
          </div>
        </div>

        <div className="o-column o-column--gap-size_200 u-margin--top_400 o-column--vertical-align_start">
          <label>{t("bookName")}</label>
          <CustomInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            flex
          />
        </div>
      </div>
      {errorBooks && !loadingBooks && <ErrorBox>{t(errorBooks)}</ErrorBox>}
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500 u-margin--top_500">
        {!loadingBooks && booksData.length === 0 && !errorBooks && (
          <InformationBox>{t("noSuppliesFound")}</InformationBox>
        )}
        {loadingBooks && <Loader size={LOADER_SIZES.MEDIUM} />}
        {isThereBooks && (
          <>
            <SectionDivider title={t("theBooks")} />
            <CollapsibleList>
              {booksData.map((schoolClass) => (
                <CollapsibleGroup key={schoolClass.id} defaultCollapsed={false}>
                  <div className="c-fabric-block">
                    <CollapsibleHeader icon={arrowIcon}>
                      <CollapsibleLabel
                        label={"academicLevel"}
                        value={schoolClass.name}
                      />
                    </CollapsibleHeader>
                    <CollapsibleContent>
                      <Table>
                        <TableHeader>
                          {bookHeaders.map((header) => (
                            <TableHeaderCell
                              key={header.key}
                              label={header.label}
                            />
                          ))}
                        </TableHeader>
                        <TableBody>
                          {schoolClass.products?.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Link
                                  to={`/delivered-supplies/${item.id}?classId=${schoolClass.id}&type=book`}
                                  className="link"
                                >
                                  {item.name}
                                </Link>
                              </TableCell>
                              <TableCell>{t(item.productSubType)}</TableCell>
                              <TableCell>{item.amountInStock}</TableCell>
                              <TableCell>{item.sellCount}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CollapsibleContent>
                  </div>
                </CollapsibleGroup>
              ))}
            </CollapsibleList>
          </>
        )}
      </div>
    </>
  );
}

function SupplieUniform() {
  const { t } = useTranslation("general");
  const [uniformName, setUniformName] = useState("");
  const [sizeName, setSizeName] = useState("");
  const uniformNameValue = useDebounce(uniformName, 500);
  const sizeNameValue = useDebounce(sizeName, 500);
  const [loadingUniform, setLoadingUniform] = useState(false);
  const [uniformsData, setUniformsData] = useState([]);
  const [errorUniform, setErrorUniform] = useState(false);

  // Configuration
  const uniformHeaders = [
    { key: "size", label: "size", type: "text" },
    { key: "amountInStock", label: "amountInStock", type: "text" },
    {
      key: "deliveredCount",
      label: "deliveredCount",
    },
  ];

  const getAllUniforms = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setErrorUniform(false);
      setLoadingUniform(true);
      setUniformsData([]);
      const response = await axiosClient.get("/productRuler/withSizes", {
        params: {
          productRulerName: uniformNameValue,
          itemSize: sizeNameValue,
          organizationId,
          limit: 10000,
        },
      });

      if (response.status === 200 && response.data) {
        const items = response.data.productRulers?.map((item) => ({
          id: item.id,
          name: item.name,
          color: item.color,
          sizes: item.productRulerItem?.map((item) => ({
            id: item.id,
            size: item.size,
            sellPrice: item.sellPrice,
            notes: item.notes,
            amountInStock: item.quantity,
            quantity: "",
            sellCount: item.sellCount,
            // deliveredCount: Math.floor(Math.random() * 5) + 1,
          })),
        }));
        setUniformsData(items);
      }
    } catch (err) {
      setErrorUniform("errorGettingUniforms");
    } finally {
      setLoadingUniform(false);
    }
  };

  useEffect(() => {
    getAllUniforms();
  }, [uniformNameValue, sizeNameValue]);

  const isThereUniforms = !loadingUniform && uniformsData.length > 0 && !errorUniform;
  return (
    <>
      <div className="o-column-row-switcher o-column--vertical-align_start o-row--gap-size_300">
        <div className="o-column o-column--gap-size_200 u-margin--top_400 o-column--vertical-align_start">
          <label>{t("uniformName")}</label>
          <CustomInput
            value={uniformName}
            onChange={(e) => setUniformName(e.target.value)}
            flex
          />
        </div>
        <div className="o-column o-column--gap-size_200 u-margin--top_400 o-column--vertical-align_start">
          <label>{t("size")}</label>
          <CustomInput
            value={sizeName}
            onChange={(e) => setSizeName(e.target.value)}
            flex
          />
        </div>
      </div>
      {errorUniform && <ErrorBox>{t(errorUniform)}</ErrorBox>}
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500 u-margin--top_500">
        {loadingUniform && <Loader size={LOADER_SIZES.MEDIUM} />}
        {!loadingUniform && uniformsData.length === 0 && !errorUniform && (
          <InformationBox>{t("noSuppliesFound")}</InformationBox>
        )}
        {isThereUniforms && (
          <>
            <SectionDivider title={t("uniforms")} />
            <CollapsibleList>
              {uniformsData.map((group) => (
                <CollapsibleGroup key={group} defaultCollapsed={false}>
                  <div className="c-fabric-block">
                    <CollapsibleHeader icon={arrowIcon}>
                      <CollapsibleLabel
                        label={"uniformName"}
                        value={group.name}
                      />
                      <CollapsibleLabel label={"color"} value={group.color} />
                    </CollapsibleHeader>

                    <CollapsibleContent>
                      <Table>
                        <TableHeader>
                          {uniformHeaders.map((header) => (
                            <TableHeaderCell
                              key={header.key}
                              label={header.label}
                            />
                          ))}
                        </TableHeader>
                        <TableBody>
                          {group.sizes?.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <Link
                                  typ="link"
                                  to={`/delivered-supplies/${item.id}?type=uniform`}
                                >
                                  {item.size}
                                </Link>
                              </TableCell>
                              <TableCell>{item.amountInStock}</TableCell>
                              <TableCell>{item.sellCount}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CollapsibleContent>
                  </div>
                </CollapsibleGroup>
              ))}
            </CollapsibleList>
          </>
        )}
      </div>
    </>
  );
}

export default DeliveredSuppliesPage;
